import { Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { getErrorMessage } from "api/errors";
import { useSettings } from "api/general";
import { useAppSelector } from "app/hooks";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { OLModal } from "components/Tooltips/OLModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Formik } from "formik";
import { useMessage } from "hooks/intl";
import { Form } from "react-router-dom";
import { getTimezones } from "reduxState/settings/settings";
import { alphabetizeByProperty } from "utilities/arrays";
import { useStyles } from "./StoreList.styles";

interface Props {
  isModalOpen: boolean;
  selectedStoreCode: string;
  closeModal: () => void;
}

export function ManageTimezoneModal({
  selectedStoreCode,
  isModalOpen,
  closeModal,
}: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { useTenantSettings, updateSettings } = useSettings();
  const { data: settingsBySelectedTenant } =
    useTenantSettings(selectedStoreCode);

  const timezones = useAppSelector(getTimezones);

  const timezoneItems = alphabetizeByProperty(
    timezones?.map((timezone) => ({
      id: timezone.timezone,
      label: timezone.place,
    })),
    "label",
  );

  return (
    <OLModal
      open={isModalOpen}
      className={classes.timezoneModal}
      header={message({ id: "timezone" })}
      onClose={() => closeModal()}
      allowOverflow
    >
      <>
        <Typography className={classes.timezoneModalInfo}>
          {message({ id: "timezoneInfo" })}
        </Typography>
        {settingsBySelectedTenant && selectedStoreCode && (
          <Formik
            initialValues={{
              timezone: settingsBySelectedTenant?.settings?.timezone,
            }}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              try {
                const response = await updateSettings(
                  {
                    ...values,
                  },
                  selectedStoreCode,
                );
                if (response?.data) {
                  closeModal();
                  notify(message({ id: "timezoneSaved" }));
                }
              } catch (exception) {
                setSubmitting(false);
                notify(getErrorMessage(exception));
              }
            }}
          >
            {({ handleSubmit, setFieldValue, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                <SelectInput
                  displaySearch
                  items={timezoneItems}
                  label={
                    <RequiredFieldLabel text={message({ id: "timezone" })} />
                  }
                  name="timezone"
                  onChange={(e) => setFieldValue("timezone", e.target.value)}
                  data-test-id="manage-timezone-input"
                />
                <OLButton
                  className={classes.timezoneModalButton}
                  color="primary"
                  disabled={isSubmitting}
                  fullWidth
                  type="submit"
                  variant="contained"
                  data-test-id="manage-timezone-save-button"
                >
                  {message({ id: "save" })}
                </OLButton>
              </Form>
            )}
          </Formik>
        )}
      </>
    </OLModal>
  );
}
